import { useEffect, useRef, useState } from 'react';
import { ReactComponent as KebabSvg } from '../../../assets/svg/kebab.svg';
import { ReactComponent as LoadingSvg } from '../../../assets/svg/loading-half-status.svg';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/20/solid';
import UpdateDialog from './updateDialog';
import request from '../../../lib/request';
import ViewIngredient from './viewIngredient';
import Title from '../../../components/dialog/title';

const getSubtotal = (key, list = []) => {
  return list.reduce((acc, cur) => {
    return acc + (cur[key] ?? 0);
  }, 0);
};

const ViewPhoto = ({
  viewDialogRef,
  closeDialog,
  mealInfo,
  handleRefresh,
  isIngredientDialogOpen,
  setIsIngredientDialogOpen,
  user,
  openPayDialog,
}) => {
  const bottomDialogRef = useRef(null);
  const updateDialogRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [totalCalories, setTotalCalories] = useState({
    calories: 0,
    proteins: 0,
    carbohydrates: 0,
    fats: 0,
  });

  useEffect(() => {
    if (mealInfo && mealInfo.id) {
      setIngredients(mealInfo.ingredients);
      setTotalCalories({
        calories: mealInfo?.calorie ?? 0,
        proteins: getSubtotal('proteins', mealInfo.ingredients).toFixed(1),
        carbohydrates: getSubtotal(
          'carbohydrates',
          mealInfo.ingredients
        ).toFixed(1),
        fats: getSubtotal('fats', mealInfo.ingredients).toFixed(1),
      });
    }
  }, [mealInfo]);

  const handleClose = () => {
    closeBottomDialog();
    closeDialog();
  };

  const handleDelete = async () => {
    try {
      if (!window.confirm('Are you sure you want to delete this item?')) {
        return;
      }

      setLoading(true);

      const { result } = await request.delete(`/meal/${mealInfo.id}`);

      if (!result) {
        return alert('Failed to delete.');
      }

      await handleRefresh();
      handleClose();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = () => {
    if (updateDialogRef.current) {
      updateDialogRef.current.showModal();
      setIsUpdateDialogOpen(true);
      closeBottomDialog();
    }
  };

  const closeUpdateDialog = () => {
    if (updateDialogRef.current) {
      updateDialogRef.current.close();
      setIsUpdateDialogOpen(false);
    }
  };

  const openBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.showModal();
    }
  };

  const closeBottomDialog = () => {
    if (bottomDialogRef.current) {
      bottomDialogRef.current.close();
      setIsIngredientDialogOpen(false);
    }
  };

  const openEditMenu = () => {
    openBottomDialog();
    setIsIngredientDialogOpen(false);
  };

  const openIngredientList = () => {
    openBottomDialog();
    setIsIngredientDialogOpen(true);
  };

  return (
    mealInfo?.id && (
      <dialog
        ref={viewDialogRef}
        className='w-full h-full max-w-lg max-h-full p-4 bg-black'
      >
        <Title
          title={`${mealInfo.logDate} ${mealInfo.logTime}`}
          onClick={handleClose}
          color='white'
        />
        <div className='relative flex justify-center mb-2 rounded-xl border border-tertiary overflow-hidden'>
          <img
            src={mealInfo?.photoUrl}
            alt=''
            className='size-full object-contain'
          />
          <div className='absolute top-2 right-2 bg-secondary/70 p-2 rounded-full'>
            <KebabSvg
              onClick={openEditMenu}
              fill='white'
            />
          </div>
        </div>
        <div className='space-y-4 text-xs'>
          <div className='flex items-center flex-wrap gap-1 text-white'>
            {mealInfo?.tags &&
              mealInfo.tags.length > 0 &&
              mealInfo.tags.map((item) => (
                <div
                  key={item.tag.id}
                  className='bg-tertiary rounded-full px-1'
                >
                  #{item.tag.name}
                </div>
              ))}
          </div>
          <div className='grid grid-cols-2 gap-1 rounded-xl text-white'>
            <div className='flex-1 flex justify-between items-center border rounded-full p-2 bg-tertiary/30'>
              <div>Calories</div>
              <div className='font-bold'>{totalCalories.calories} Cal</div>
            </div>
            <div className='flex justify-between items-center gap-2 border rounded-full p-2 border-protein bg-protein/30'>
              <div>Protein</div>
              <div className='font-bold'>{totalCalories.proteins} g</div>
            </div>
            <div className='flex justify-between items-center gap-2 border rounded-full p-2 border-carbs bg-carbs/30'>
              <div>Carbs</div>
              <div className='font-bold'>{totalCalories.carbohydrates} g</div>
            </div>
            <div className='flex justify-between items-center gap-2 border rounded-full p-2 border-fats bg-fats/30'>
              <div>Fat</div>
              <div className='font-bold'>{totalCalories.fats} g</div>
            </div>
          </div>
          <div className='flex justify-center items-center my-4 text-white'>
            <a
              className='underline cursor-pointer'
              onClick={openIngredientList}
            >
              View ingredients
            </a>
          </div>
          {mealInfo?.description && (
            <div className='break-all text-white text-xs bg-tertiary rounded-xl p-2'>
              {mealInfo.description}
            </div>
          )}
        </div>
        <dialog
          ref={bottomDialogRef}
          className='w-full h-full max-w-lg max-h-full bg-transparent'
          onCancel={closeBottomDialog}
        >
          <div
            className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 pb-6 pt-4 text-white`}
          >
            <div className='text-right text-2xl mr-1'>
              <span onClick={closeBottomDialog}>🅧</span>
            </div>
            {isIngredientDialogOpen ? (
              <ViewIngredient
                ingredients={ingredients}
                total={totalCalories}
              />
            ) : (
              <div className='text-sm space-y-4'>
                <button
                  className='px-3 py-2 flex items-center gap-2'
                  onClick={openUpdateDialog}
                  disabled={loading}
                >
                  <PencilSquareIcon className='size-5 opacity-40' />
                  Edit current meal log
                </button>
                <button
                  className='px-3 py-2 flex items-center gap-2'
                  onClick={handleDelete}
                  disabled={loading}
                >
                  <TrashIcon className='size-5 opacity-40' />
                  Delete current meal log
                </button>
              </div>
            )}
            {loading && (
              <div className='absolute animate-bounce inset-x-0 top-4 flex justify-center items-center'>
                <LoadingSvg className='animate-spin' />
              </div>
            )}
          </div>
        </dialog>
        <UpdateDialog
          dialogRef={updateDialogRef}
          closeDialog={closeUpdateDialog}
          mealInfo={mealInfo}
          isUpdateDialogOpen={isUpdateDialogOpen}
          handleRefresh={handleRefresh}
          user={user}
          openPayDialog={openPayDialog}
        />
      </dialog>
    )
  );
};

export default ViewPhoto;
